import { iatan2, idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The Maw (La Orilla 3, docs/SAGAS.md): a fish of ink the size of a ship, at the edge of the fin's tip, with a light
 * hanging over its mouth on a stalk. Its rule: while the light shines it drags you. Every hero who sees it is pulled
 * slowly towards the mouth, and the mouth bites whoever gets close. Breaking the stalk puts the light out a while: the
 * Maw is blind and still, doesn't bite, and takes its blows full; lit, it shrugs most of them off. The lighthouse is
 * the node, with braziers round it: the Drowned stood by one put it out, a hero by it lights it again, and every one
 * lit halves the pull a share. The Maw felled wins it; the lighthouse fallen loses it.
 */

const maw = (s: BattleState): Enemy | undefined => (s.maw ? s.enemies.find((e) => e.id === s.maw?.id && e.hp > 0) : undefined);

export const mawTick = (s: BattleState): void => {
  const r = s.rules?.maw;
  if (!r) return;
  const st = (s.maw ??= { id: 0, stalk: 0, dark: 0, lit: r.braziers.map(() => true), douse: r.braziers.map(() => 0), done: 0 });
  if (!s.started || st.done) return;
  if (!st.id) {
    // It rises at the edge a moment in, its light already lit.
    if (s.waveT < r.rise) return;
    const e = spawnEnemy(s, 'maw', 1, false, 0, r.at.x, r.at.y, r.hp);
    e.facing = iatan2(-r.at.y, -r.at.x);
    st.id = e.id;
    st.stalk = spawnEnemy(s, 'lure', 1, false, 0, r.stalkAt.x, r.stalkAt.y, r.stalkHp).id;
    ev(s, { t: 'maw', kind: 'rise', x: r.at.x, y: r.at.y });
  }
  const m = maw(s);
  if (!m) {
    st.done = s.tick;
    for (const e of s.enemies) if (e.kind === 'lure') e.hp = 0;
    ev(s, { t: 'maw', kind: 'felled', x: r.at.x, y: r.at.y });
    return;
  }
  // The Drowned come up out of the sea all fight long.
  if (r.shore.length && s.waveT % r.drowned.every === 0) {
    const at = r.shore[rngInt(s.rng, r.shore.length)];
    if (at) spawnEnemy(s, 'drowned', 1, false, 0, at.x, at.y, mulBp(r.drowned.hp, s.rules?.enemyHpBp ?? 10000));
  }
  // The braziers: put out by a Drowned stood by one with no hero there; lit again by a hero.
  r.braziers.forEach((b, i) => {
    const hero = s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, b.x, b.y, r.reach));
    const drowned = s.enemies.some((e) => e.hp > 0 && e.kind === 'drowned' && withinRange(e.x, e.y, b.x, b.y, r.reach));
    if (st.lit[i]) {
      st.douse[i] = drowned && !hero ? (st.douse[i] ?? 0) + 1 : 0;
      if ((st.douse[i] ?? 0) >= r.douse) {
        st.lit[i] = false;
        st.douse[i] = 0;
        ev(s, { t: 'maw', kind: 'out', x: b.x, y: b.y });
      }
    } else {
      st.douse[i] = hero && !drowned ? (st.douse[i] ?? 0) + 1 : 0;
      if ((st.douse[i] ?? 0) >= r.relight) {
        st.lit[i] = true;
        st.douse[i] = 0;
        ev(s, { t: 'maw', kind: 'lit', x: b.x, y: b.y });
      }
    }
  });
  // The stalk broken: dark a while, then it grows back.
  if (!st.dark && !s.enemies.some((e) => e.id === st.stalk && e.hp > 0)) {
    st.dark = s.tick + r.dark;
    ev(s, { t: 'maw', kind: 'dark', x: r.stalkAt.x, y: r.stalkAt.y });
  }
  if (st.dark) {
    if (s.tick < st.dark) return;
    st.dark = 0;
    st.stalk = spawnEnemy(s, 'lure', 1, false, 0, r.stalkAt.x, r.stalkAt.y, r.stalkHp).id;
    ev(s, { t: 'maw', kind: 'light', x: r.stalkAt.x, y: r.stalkAt.y });
  }
  // Lit: it drags whoever sees it, less for every brazier lit, and bites whoever is at its mouth.
  const lit = st.lit.filter(Boolean).length;
  const pull = mulBp(r.pull, 10000 - idiv(r.braziersBp * lit, imax(1, r.braziers.length)));
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !withinRange(h.x, h.y, m.x, m.y, r.sight)) continue;
    if (withinRange(h.x, h.y, m.x, m.y, r.bite.range)) continue;
    const o = { x: h.x, y: h.y, facing: h.facing };
    stepToward(o, m.x, m.y, pull);
    h.x = o.x;
    h.y = o.y;
  }
  if (m.cd > 0) return;
  const near = s.heroes.filter((h) => h.downTicks === 0 && withinRange(h.x, h.y, m.x, m.y, r.bite.range));
  if (!near.length) return;
  const h = near[rngInt(s.rng, near.length)];
  if (!h) return;
  m.cd = r.bite.every;
  m.anim = 'attack';
  m.animT = 0;
  m.facing = iatan2(h.y - m.y, h.x - m.x);
  damageHero(s, h, imax(r.bite.damage, mulBp(h.stats.maxHp, r.bite.maxHpBp)), m);
  ev(s, { t: 'maw', kind: 'bite', x: h.x, y: h.y });
};

/** What a blow on the Maw lands as: little while its light shines, full and more while it is dark. */
export const mawTakenBp = (s: BattleState): number => {
  const r = s.rules?.maw;
  if (!r) return 10000;
  return s.maw?.dark ? r.darkBp : r.litBp;
};

/** Whether the Maw's light is out (blind and still). */
export const mawDark = (s: BattleState): boolean => !!s.maw?.dark;

/** The Maw felled: won (a moment later); the lighthouse fallen: lost. */
export const mawResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.maw;
  if (!r) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.maw?.done && s.tick - s.maw.done >= r.beat ? 'won' : null;
};
